import type { StockMovementDto } from "@grizzly/shared";
import { formatDateTime } from "../../lib/format.js";
import { Badge } from "../ui/badge.js";
import { Button } from "../ui/button.js";
import { Undo2 } from "lucide-react";
import { useI18n, type MessageKey } from "../../i18n/index.js";

const KIND_KEYS: Record<string, MessageKey> = {
  opening: "inventory.kind.opening",
  receipt: "inventory.kind.receipt",
  issue: "inventory.kind.issue",
  return: "inventory.kind.return",
  adjustment_in: "inventory.kind.adjustment_in",
  adjustment_out: "inventory.kind.adjustment_out",
};

export function StockMovementList({
  movements,
  onReverse,
}: {
  movements: StockMovementDto[];
  onReverse(movement: StockMovementDto): void;
}) {
  const { t } = useI18n();

  const kindLabel = (kind: string): string => {
    const key = KIND_KEYS[kind];
    return key ? t(key) : kind;
  };

  return (
    <ul className="space-y-2">
      {movements.map((m) => (
        <li
          key={m.id}
          className="flex items-center justify-between gap-2 rounded border border-line px-3 py-2"
        >
          <div className="min-w-0">
            <p className="flex items-center gap-2 text-sm">
              <Badge tone={m.direction === "in" ? "complete" : "risk"}>
                {m.direction === "in" ? "+" : "-"}
                {m.quantity}
              </Badge>
              <span className="font-medium text-ink">{kindLabel(m.kind)}</span>
              {m.reason ? (
                <span className="text-xs text-ink-muted">— {m.reason}</span>
              ) : null}
            </p>
            <p className="text-xs text-ink-muted">
              {formatDateTime(m.occurredAt)} · {m.actorName} · {m.locationName}
            </p>
          </div>
          {!m.reversalOfId ? (
            <Button size="sm" variant="ghost" onClick={() => onReverse(m)}>
              <Undo2 className="h-4 w-4 rtl:-scale-x-100" aria-hidden="true" />{" "}
              {t("inventory.reverse")}
            </Button>
          ) : (
            <span className="text-xs text-ink-muted">{t("inventory.reversed")}</span>
          )}
        </li>
      ))}
    </ul>
  );
}
